/**
 * useAllowance — reads the ERC-20 allowance the connected wallet has
 * granted to the AsicExecutor contract. Polls automatically via
 * wagmi's refetchInterval so it stays fresh across blocks.
 */

import { useReadContract, useAccount } from "wagmi";
import type { Address } from "viem";
import { ERC20_ABI, ASIC_EXECUTOR_ADDRESS } from "@/lib/contracts";

export function useAllowance(tokenAddress: Address | undefined) {
  const { address } = useAccount();

  const result = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: "allowance",
    args: address ? [address, ASIC_EXECUTOR_ADDRESS] : undefined,
    blockTag: "latest",
    query: {
      enabled: !!address && !!tokenAddress,
      refetchInterval: 12_000, // ~1 block on mainnet
    },
  });

  return {
    allowance: result.data as bigint | undefined,
    isLoading: result.isLoading,
    refetch: result.refetch,
    /** Convenience: pass the parsed swap amount to check sufficiency. */
    needsApproval: (amount: bigint) => {
      if (result.data === undefined) return false;
      return (result.data as bigint) < amount;
    },
  };
}
